import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';
import './ChatWidget.css';

const CHAT_GUEST_ID_KEY = 'chatGuestId';

function getOrCreateGuestId() {
  let id = localStorage.getItem(CHAT_GUEST_ID_KEY);
  if (!id) {
    id = 'g_' + crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem(CHAT_GUEST_ID_KEY, id);
  }
  return id;
}

function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const channelRef = useRef(null);

  const userId = localStorage.getItem('userId') || null;
  const guestId = userId ? null : getOrCreateGuestId();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getOrCreateConversation = async () => {
    setLoading(true);
    setError('');
    try {
      if (userId) {
        const { data: existing } = await supabase
          .from('chat_conversations')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();
        if (existing) {
          setConversation(existing);
          await fetchMessages(existing.id);
          subscribe(existing.id);
          setLoading(false);
          return;
        }
      } else {
        const { data: existing } = await supabase
          .from('chat_conversations')
          .select('id')
          .eq('guest_id', guestId)
          .maybeSingle();
        if (existing) {
          setConversation(existing);
          await fetchMessages(existing.id);
          subscribe(existing.id);
          setLoading(false);
          return;
        }
      }

      const { data: inserted, error: insErr } = await supabase
        .from('chat_conversations')
        .insert([{ user_id: userId || null, guest_id: userId ? null : guestId }])
        .select('id')
        .single();

      if (insErr) throw insErr;
      setConversation(inserted);
      setMessages([]);
      subscribe(inserted.id);
    } catch (e) {
      console.error('Chat getOrCreateConversation:', e);
      setError('Unable to start chat. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    const { data, error: e } = await supabase
      .from('chat_messages')
      .select('id, sender_type, message_text, created_at')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: true });
    if (!e) setMessages(data || []);
  };

  const subscribe = (convId) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    const ch = supabase
      .channel('chat-' + convId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: 'conversation_id=eq.' + convId }, (payload) => {
        setMessages((m) => [...m, payload.new]);
      })
      .subscribe();
    channelRef.current = ch;
  };

  useEffect(() => {
    if (open && !conversation && !loading) getOrCreateConversation();
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [open]);

  const send = async () => {
    const text = (input || '').trim();
    if (!text || !conversation || sending) return;
    setSending(true);
    setInput('');
    setError('');
    try {
      const { error: e } = await supabase.from('chat_messages').insert([
        { conversation_id: conversation.id, sender_type: 'user', message_text: text }
      ]);
      if (e) throw e;
    } catch (e) {
      console.error('Chat send:', e);
      setError('Failed to send. Try again.');
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!userId) getOrCreateGuestId();
  };

  return (
    <>
      <button
        className="chat-widget-toggle"
        onClick={handleOpen}
        aria-label="Open chat"
        title="Chat with us"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      </button>

      {open && (
        <div className="chat-widget-panel">
          <div className="chat-widget-header">
            <h3>Chat with us</h3>
            <button className="chat-widget-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
          </div>
          <div className="chat-widget-body">
            {loading && (
              <div className="chat-widget-loading">Starting chat...</div>
            )}
            {error && <div className="chat-widget-error">{error}</div>}
            {!loading && conversation && (
              <div className="chat-widget-messages">
                {messages.length === 0 && (
                  <div className="chat-widget-empty">Say hello! We typically reply within a few minutes.</div>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`chat-msg ${m.sender_type === 'user' ? 'chat-msg-user' : 'chat-msg-admin'}`}>
                    <span className="chat-msg-sender">{m.sender_type === 'admin' ? 'Support' : 'You'}</span>
                    <p className="chat-msg-text">{m.message_text}</p>
                    <span className="chat-msg-time">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
          {!loading && conversation && (
            <div className="chat-widget-footer">
              <textarea
                placeholder="Type a message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={sending}
              />
              <button className="chat-widget-send" onClick={send} disabled={sending || !input.trim()}>
                Send
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default ChatWidget;
